/** AI pricing judgements → cost sheet, with history guard rails (original fillFromAI). */
import { blankSheet, cmPartsOf, DEFAULT_FX, newRow, setCmParts, withLabour } from "./costing.ts";
import { applyTiers } from "./tiers.ts";
import type { HistoryContext } from "./context.ts";
import { esc, f2, f3, n } from "./num.ts";
import { fabStats, samCM, trimStats } from "./stats.ts";
import { trimCatOf } from "./text.ts";
import { sizeBand } from "./checklist.ts";
import { L, type AllCmParts, type CmCheck, type History, type Items, type Lang, type LineRow, type PriceFabric, type PriceResult, type PriceTrim, type QCoef, type Sheet } from "./types.ts";

/** Guard-rail thresholds. */
export const GUARD = { priceLow: "p10", priceHigh: "p90", usageDev: 0.4, cmDev: 0.35 } as const;
const MAIN_FABRIC = /main|shell|body|主|大身/i;

export function fillFromAI(args: {
  pr: PriceResult; ctx: HistoryContext; items: Items; style?: { customer?: string; style_no?: string; style_name?: string; season?: string };
  demo: boolean; lang: Lang; hist: History; keepQuote?: boolean; today?: string; coef?: QCoef;
  /** tiered quote: the quantities the AI priced (JF 2026-10-12) */
  tiers?: number[];
  /** RMB per USD for the new sheet (JF 2026-10-13); default DEFAULT_FX */
  fx?: number;
}): Sheet {
  const { pr, ctx, items: it, lang, hist: h } = args;
  const s = blankSheet(args.today, args.coef);
  s.quote = !!args.keepQuote; s.from = "ai";
  const sub = pr.sub && h.sub2cat[pr.sub] ? pr.sub : ctx.sub;
  const st = args.style || {};
  s.h.customer = st.customer || ""; s.h.style = st.style_no || ""; s.h.season = st.season || "";
  s.h.name = (st.style_name ? st.style_name + " · " : "") + sub;
  s.h.qty = String((it.qty || {}).pcs || ""); s.h.co = (it.origin || {}).country || "";
  s.h.content = (it.main_fabric || {}).composition || ""; s.h.width = (it.main_fabric || {}).width || "";
  const tag = "AI" + (args.demo ? " (demo)" : "");
  const b: RowCtx = { ctx, items: it, hist: h, lang, sub, tag };
  for (const f of pr.fabrics || []) s.fab.push(fabRow(f, b));
  // Fabric mill: the supplier named in the files for the main fabric (JF 2026-09-29)
  const mainF = (pr.fabrics || []).find((f) => MAIN_FABRIC.test((f.position || "") + " " + (f.name || ""))) || (pr.fabrics || [])[0];
  s.h.mill = String((it.main_fabric || {}).supplier || (mainF && mainF.supplier) || "");
  for (const f of pr.trims || []) s.trim.push(trimRow(f, b));
  const cmr = cmFromPrice(pr, b);
  s.h.factory = cmr.factory;
  s.cm = cmr.cm;
  s.fx = args.fx && args.fx > 0 ? args.fx : DEFAULT_FX;
  // the CM is everything paid to the factory (JF 2026-10-13): cutting / sewing / packing as the AI split them, its
  // processes; customs & document fees are left empty for the AM (JF 2026-10-16: no fixed ¥3,000 any more)
  const parts = withLabour(withProcesses(cmPartsOf(s), pr), pr.cm_split, n(s.cm.v));
  setCmParts(s, parts);
  s.test = pr.testing_total ? String(pr.testing_total) : "";
  s.insp = pr.inspection_total ? String(pr.inspection_total) : "";
  s.ai = { items: it, refs: pr.ref_scs || [], demo: args.demo, sub };
  s.origin = L(lang, "AI estimate", "AI 估价") + (args.demo ? " · demo" : "");
  if (args.tiers?.length) applyTiers(s, pr, args.tiers);
  return s;
}

/** What a row / CM builder needs to know. `tag` is the source label ("AI", "AI (demo)", "AI · re-estimated"…). */
export interface RowCtx { ctx: HistoryContext; items: Items; hist: History; lang: Lang; sub: string; tag: string }

/** Sizes are judged by the AI (偏差 D9); only an unusual range is surfaced. */
function unusualSizes(it: Items): { count: number; band: "few" | "normal" | "many"; extended: boolean } | null {
  const sz = it.sizes || {};
  const band = sizeBand(sz.count);
  const unusual = band ? band !== "normal" || !!sz.extended : !!sz.extended;
  return unusual ? { count: Number(sz.count) || 0, band: band || "normal", extended: !!sz.extended } : null;
}

/** One AI fabric judgement → a sheet row with the history guard rails (price P10–P90, main-fabric yield ±40%). */
export function fabRow(f: PriceFabric, b: RowCtx): LineRow {
  const { hist: h, lang, sub, tag, ctx } = b;
  const r = newRow("fab");
  r.name = f.name || ""; r.pos = f.position || ""; r.qual = f.quality || ""; r.unit = f.unit || "m";
  r.cat = f.fab_cat && h.fabGlobal[f.fab_cat] ? f.fab_cat : ""; r.px = f3(f.px); r.yld = f3(f.yield); r.rem = f.basis || "";
  const fs = r.cat ? fabStats(h, sub, r.cat) : null;
  const fl: string[] = [];
  if (fs && n(f.px) && (n(f.px) < fs.p10! || n(f.px) > fs.p90!))
    fl.push(L(lang, "Price outside history P10–P90 ($" + fs.p10 + "–" + fs.p90 + ", " + fs.n + " records)", "单价超出历史 P10–P90（$" + fs.p10 + "–" + fs.p90 + "，" + fs.n + " 条）"));
  const um = ctx.usage_median_m;
  const main = MAIN_FABRIC.test(r.pos + " " + r.name);
  if (um && r.unit === "m" && main && n(f.yield) && Math.abs(n(f.yield) - um) / um > GUARD.usageDev)
    fl.push(L(lang, "Yield differs >40% from the sub-category median " + um + " m", "用量与同子类中位数 " + um + " m 相差超过 40%"));
  r.flag = fl.join(" · ");
  const sz = unusualSizes(b.items);
  r.src = {
    name: { t: "ai", d: tag },
    px: { t: "ai", d: tag + " · " + (f.basis || "") + (fs ? " · " + L(lang, "history", "历史") + " P25–P75 $" + fs.p25 + "–" + fs.p75 : "") },
    yld: { t: "ai", d: tag + (um ? " · " + L(lang, "sub-category median", "同子类中位数") + " " + um + " m" : "") + (main && sz ? " · " + sizesNote(sz.count, sz.band, sz.extended, lang) : "") },
  };
  return r;
}

/** One AI trim judgement → a sheet row with the price guard rail. */
export function trimRow(f: PriceTrim, b: RowCtx): LineRow {
  const { hist: h, lang, sub, tag } = b;
  const r = newRow("trim");
  r.name = f.name || ""; r.pos = f.position || ""; r.qual = f.quality || ""; r.cat = trimCatOf(f.trim_cat, h);
  r.px = f3(f.px); r.yld = f3(f.yield); r.rem = f.basis || ""; r.supp = f.supplier || "";
  const ts = r.cat ? trimStats(h, sub, r.cat) : null;
  if (ts && n(f.px) && (n(f.px) < ts.p10! || n(f.px) > ts.p90!))
    r.flag = L(lang, "Price outside history P10–P90 ($" + ts.p10 + "–" + ts.p90 + ")", "单价超出历史 P10–P90（$" + ts.p10 + "–" + ts.p90 + "）");
  r.src = {
    name: { t: "ai", d: tag },
    px: { t: "ai", d: tag + " · " + (f.basis || "") + (ts ? " · " + L(lang, "history median", "历史中位数") + " $" + ts.med : "") },
    yld: { t: "ai", d: tag },
  };
  return r;
}

export type CmReply = Pick<PriceResult, "sam" | "city" | "cm_base" | "cm_split" | "rush_pct" | "cm_reason_en" | "cm_reason_zh">;

/** CM = cm_base × (1 + rush%), cross-checked against the sub-category median and the SAM engine. */
export function cmFromPrice(pr: CmReply, b: RowCtx): { cm: Sheet["cm"]; factory: string } {
  const { ctx, items: it, hist: h, lang, sub, tag } = b;
  const rush = n(pr.rush_pct);
  const cm = n(pr.cm_base) * (1 + rush / 100);
  const eng = samCM(h, pr.sam || (it.complexity || {}).sam_est, pr.city || (it.origin || {}).city, (it.qty || {}).pcs);
  const check: CmCheck = {
    ai: { cm, rush, reason_en: pr.cm_reason_en || "", reason_zh: pr.cm_reason_zh || "" },
    ...cmRefs(ctx, it, h, sub, cm, eng),
  };
  const reason = lang === "zh" ? pr.cm_reason_zh || pr.cm_reason_en || "" : pr.cm_reason_en || pr.cm_reason_zh || "";
  return {
    factory: pr.city || (eng ? eng.city : ""),
    cm: { v: f2(cm), note: reason.slice(0, 160), src: { t: "ai", d: tag + " · SAM " + (pr.sam || "?") + " · " + (pr.city || "") }, q: null, chk: "", check },
  };
}

/**
 * The processes of an AI reply into the CM parts (JF 2026-10-09: washing / embroidery are paid by the factory by default;
 * the AM can move them to Additional). Older replies priced them under "additional" — they go into the CM too.
 * A process the reply does not mention keeps its current value.
 */
export function withProcesses(p: AllCmParts, pr: Pick<PriceResult, "process" | "process_desc" | "additional" | "additional_desc">): AllCmParts {
  const out = structuredClone(p);
  const pc = pr.process, pd = pr.process_desc || {};
  for (const k of ["wash", "emb", "other"] as const) {
    if (pc && pc[k] != null) out[k] = { px: n(pc[k]) ? f2(pc[k]) : "", desc: n(pc[k]) ? pd[k] || "" : "" };
  }
  const ad = pr.additional || {};
  for (const k of ["wash", "emb"] as const) {
    if ((!pc || pc[k] == null) && n(ad[k])) out[k] = { px: f2(ad[k]), desc: pr.additional_desc || "" };
  }
  return out;
}

/** The reference part of the CM check (history median, SAM engine, far-off flag, unusual sizes). */
export function cmRefs(ctx: HistoryContext, it: Items, h: History, sub: string, cm: number, eng: ReturnType<typeof samCM>): Omit<CmCheck, "ai"> {
  const hs = ctx.sub_stats || {};
  const refs = [hs.cm_med, eng && eng.usd].filter((v): v is number => v != null && (v as number) > 0);
  const far = !!refs.length && refs.every((v) => Math.abs(cm - v) / v > GUARD.cmDev);
  const sz = unusualSizes(it);
  return {
    hist: hs.cm_med != null ? { sub, cat: h.sub2cat[sub] || ctx.cat, level: (h.sub[h.sub2cat[sub] || ""] || {})[sub] ? "sub" : "cat", cm_med: hs.cm_med, n: hs.n } : null,
    eng: eng ? { sam: eng.sam, city: eng.city, moq: eng.moq, usd: eng.usd } : null,
    far,
    ...(sz ? { sizes: sz } : {}),
  };
}

/** "12 sizes (many — normal is 6–8), incl. 3XL+" */
export function sizesNote(count: number, band: "few" | "normal" | "many" | null, extended: boolean, lang: Lang): string {
  const b = band === "few" ? L(lang, "few", "偏少") : band === "many" ? L(lang, "many", "偏多") : L(lang, "normal", "正常");
  const base = count ? L(lang, `${count} sizes (${b} — normal is 6–8)`, `${count} 个码（${b}，正常 6–8 个）`) : L(lang, "size count unknown", "码数未知");
  return base + (extended ? L(lang, ", incl. extended sizes (3XL+)", "，含加大码（3XL 及以上）") : "");
}

/**
 * CM cross-check as HTML, identical to the original's `cm.chk` for sub-level history.
 * For a category-level fallback (sub-categories added in 偏差 D3) the history line names the category.
 */
export function renderCmCheck(c: CmCheck | null | undefined, lang: Lang): string {
  if (!c) return "";
  const out: string[] = [];
  const reason = lang === "zh" ? c.ai.reason_zh || c.ai.reason_en : c.ai.reason_en || c.ai.reason_zh;
  out.push("<b>AI</b> $" + f2(c.ai.cm) + (c.ai.rush ? " (" + L(lang, "incl. rush", "含加急") + " +" + c.ai.rush + "%)" : "") + " — " + esc(reason));
  if (c.hist) {
    const who = c.hist.level === "sub" ? esc(c.hist.sub) : esc(c.hist.cat) + " " + L(lang, "(category)", "（品类）");
    out.push("<b>" + L(lang, "History", "历史") + "</b> " + who + " " + L(lang, "median CM", "CM 中位数") + " $" + f2(c.hist.cm_med) + " (" + c.hist.n + " SC)");
  }
  if (c.eng) out.push("<b>" + L(lang, "SAM engine", "SAM 引擎") + "</b> " + c.eng.sam + " min × " + esc(c.eng.city) + (c.eng.moq ? " · MOQ " + (c.eng.moq > 0 ? "+" : "") + Math.round(c.eng.moq * 100) + "%" : "") + " = $" + f2(c.eng.usd));
  if (c.sizes) out.push("<b>" + L(lang, "Sizes", "码数") + "</b> " + esc(sizesNote(c.sizes.count, c.sizes.band, c.sizes.extended, lang)) + " — " + L(lang, "check the AI reflected it in yield and CM.", "请确认 AI 在用量和 CM 里已考虑。"));
  if (c.far) out.push('<span style="color:#b7791f;font-weight:700">⚠ ' + L(lang, "AI CM is more than 35% away from both checks — review.", "AI 的 CM 与两个参考都相差 35% 以上——请复核。") + "</span>");
  return out.join("<br>");
}
